import { useMemo, useState } from "react"
import InnerScreenHeader from "@/components/layout/inner-screen-header"
import Loader from "@/components/layout/loader"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import {
  ApiKey,
  ApiKeyEnvironment,
  ApiKeyScope,
  ApiKeyStatus,
  ApiKeyWithSecret,
  useApiKeys,
  useApiKeysActions,
} from "@/api/api-keys"
import { format } from "date-fns"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import ButtonCopy from "@/components/ui/button-copy"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { formatApiKeyError } from "./api-key-error"

interface ApiKeysTableProps {
  apiKeys: ApiKey[]
  onRotate: (apiKey: ApiKey) => void
  onRevoke: (apiKey: ApiKey) => void
  onRename: (apiKey: ApiKey) => void
}

function getStatusBadgeVariant(status: ApiKeyStatus) {
  if (status === ApiKeyStatus.ACTIVE) {
    return "default" as const
  }

  return "secondary" as const
}

function humanizeEnv(env: ApiKeyEnvironment) {
  return env === ApiKeyEnvironment.LIVE ? "Live" : "Test"
}

function humanizeScope(scope: ApiKeyScope) {
  switch (scope) {
    case ApiKeyScope.FULL:
      return "Full access"
    case ApiKeyScope.READ_ONLY:
      return "Read only"
    case ApiKeyScope.CHECKOUT:
    default:
      return "Checkout"
  }
}

function formatDate(value: string | null) {
  if (!value) {
    return "Never"
  }

  return format(new Date(value), "MMM d, yyyy HH:mm")
}

function ApiKeysTable({ apiKeys, onRotate, onRevoke, onRename }: ApiKeysTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="pl-6">Label</TableHead>
          <TableHead>Key prefix</TableHead>
          <TableHead>Scope</TableHead>
          <TableHead>Environment</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apiKeys.map((apiKey) => (
          <TableRow key={apiKey.id}>
            <TableCell className="pl-6 font-medium">{apiKey.label}</TableCell>
            <TableCell>{`${apiKey.prefix}…`}</TableCell>
            <TableCell>{humanizeScope(apiKey.scope)}</TableCell>
            <TableCell>{humanizeEnv(apiKey.env)}</TableCell>
            <TableCell>{formatDate(apiKey.lastUsedAt)}</TableCell>
            <TableCell>{formatDate(apiKey.createdAt)}</TableCell>
            <TableCell>
              <Badge variant={getStatusBadgeVariant(apiKey.status)}>
                {apiKey.status === ApiKeyStatus.ACTIVE ? "Active" : "Revoked"}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <OptionsDropdownMenu>
                {apiKey.status === ApiKeyStatus.ACTIVE && (
                  <DropdownMenuItem onSelect={() => onRotate(apiKey)}>
                    Rotate key
                  </DropdownMenuItem>
                )}
                {apiKey.status === ApiKeyStatus.ACTIVE && (
                  <DropdownMenuItem onSelect={() => onRename(apiKey)}>
                    Rename
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() => onRevoke(apiKey)}
                  disabled={apiKey.status === ApiKeyStatus.REVOKED}
                >
                  Revoke
                </DropdownMenuItem>
              </OptionsDropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {apiKeys.length === 0 && (
          <TableRow>
            <TableCell colSpan={8} className="py-6 text-center text-gray-300">
              No API keys yet. Create one to get started.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

export default function ApiKeysScreen() {
  const { apiKeys, isLoading } = useApiKeys()
  const { create, rotate, revoke, rename } = useApiKeysActions()

  const [createOpen, setCreateOpen] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createLabel, setCreateLabel] = useState("")
  const [createEnv, setCreateEnv] = useState(ApiKeyEnvironment.TEST)
  const [createScope, setCreateScope] = useState(ApiKeyScope.CHECKOUT)

  const [renameTarget, setRenameTarget] = useState<ApiKey | null>(null)
  const [renameLabel, setRenameLabel] = useState("")
  const [renameLoading, setRenameLoading] = useState(false)

  const [rotateTarget, setRotateTarget] = useState<ApiKey | null>(null)
  const [rotateLoading, setRotateLoading] = useState(false)

  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [revokeLoading, setRevokeLoading] = useState(false)

  const [revealKey, setRevealKey] = useState<ApiKeyWithSecret | null>(null)

  const sortedKeys = useMemo(() => {
    if (!apiKeys) {
      return []
    }

    return [...apiKeys].sort((left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    )
  }, [apiKeys])

  const resetCreateForm = () => {
    setCreateLabel("")
    setCreateEnv(ApiKeyEnvironment.TEST)
    setCreateScope(ApiKeyScope.CHECKOUT)
  }

  const handleCreate = async () => {
    if (!createLabel.trim()) {
      toast.error("Label is required")
      return
    }

    try {
      setCreateLoading(true)
      const result = await create({
        label: createLabel.trim(),
        env: createEnv,
        scope: createScope,
      })
      setRevealKey(result)
      toast.success("API key created")
      setCreateOpen(false)
      resetCreateForm()
    } catch (error) {
      toast.error(formatApiKeyError(error, "Failed to create API key"))
    } finally {
      setCreateLoading(false)
    }
  }

  const handleRotate = async () => {
    if (!rotateTarget) {
      return
    }

    try {
      setRotateLoading(true)
      const result = await rotate(rotateTarget.id)
      setRevealKey(result)
      toast.success("API key rotated")
      setRotateTarget(null)
    } catch (error) {
      toast.error(formatApiKeyError(error, "Failed to rotate API key"))
    } finally {
      setRotateLoading(false)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) {
      return
    }

    try {
      setRevokeLoading(true)
      await revoke(revokeTarget.id)
      toast.success("API key revoked")
      setRevokeTarget(null)
    } catch (error) {
      toast.error(formatApiKeyError(error, "Failed to revoke API key"))
    } finally {
      setRevokeLoading(false)
    }
  }

  const handleRename = async () => {
    if (!renameTarget || !renameLabel.trim()) {
      toast.error("Label is required")
      return
    }

    try {
      setRenameLoading(true)
      await rename(renameTarget.id, { label: renameLabel.trim() })
      toast.success("API key renamed")
      setRenameTarget(null)
    } catch (error) {
      toast.error(formatApiKeyError(error, "Failed to rename API key"))
    } finally {
      setRenameLoading(false)
    }
  }

  if (!apiKeys && isLoading) {
    return <Loader message="Loading API keys..." />
  }

  return (
    <div className="flex flex-col gap-6">
      <InnerScreenHeader title="Settings" to="/settings" />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">API Keys</h2>
        <Button onClick={() => setCreateOpen(true)}>Create API key</Button>
      </div>

      {apiKeys ? (
        <ApiKeysTable
          apiKeys={sortedKeys}
          onRotate={(apiKey) => setRotateTarget(apiKey)}
          onRevoke={(apiKey) => setRevokeTarget(apiKey)}
          onRename={(apiKey) => {
            setRenameTarget(apiKey)
            setRenameLabel(apiKey.label)
          }}
        />
      ) : (
        <Loader message="Loading API keys..." />
      )}

      <Dialog open={createOpen} onOpenChange={(open) => setCreateOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              Generate a new key for integrations. Secrets are shown once.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="api-key-label">Label</Label>
              <Input
                id="api-key-label"
                value={createLabel}
                onChange={(event) => setCreateLabel(event.target.value)}
                placeholder="Checkout integration"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Environment</Label>
              <Select
                value={createEnv}
                onValueChange={(value: ApiKeyEnvironment) => setCreateEnv(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ApiKeyEnvironment.TEST}>Test</SelectItem>
                  <SelectItem value={ApiKeyEnvironment.LIVE}>Live</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label>Scope</Label>
              <Select
                value={createScope}
                onValueChange={(value: ApiKeyScope) => setCreateScope(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ApiKeyScope.CHECKOUT}>Checkout</SelectItem>
                  <SelectItem value={ApiKeyScope.READ_ONLY}>Read only</SelectItem>
                  <SelectItem value={ApiKeyScope.FULL}>Full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createLoading}>
              {createLoading ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRenameTarget(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename API key</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="rename-api-key">Label</Label>
            <Input
              id="rename-api-key"
              value={renameLabel}
              onChange={(event) => setRenameLabel(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={renameLoading}>
              {renameLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={rotateTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRotateTarget(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate API key</AlertDialogTitle>
            <AlertDialogDescription>
              The current secret will be revoked immediately and a new secret will be issued.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotateLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRotate} disabled={rotateLoading}>
              {rotateLoading ? "Rotating..." : "Rotate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeTarget(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key</AlertDialogTitle>
            <AlertDialogDescription>
              This API key will stop working immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} disabled={revokeLoading}>
              {revokeLoading ? "Revoking..." : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={revealKey !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevealKey(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New API credentials</DialogTitle>
            <DialogDescription>
              Copy the secret and store it securely. You will not be able to see it again.
            </DialogDescription>
          </DialogHeader>
          {revealKey && (
            <div className="flex flex-col gap-4">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Secret key</Label>
                <div className="mt-1 flex items-center justify-between gap-2 rounded-md border bg-muted px-3 py-2">
                  <span className="truncate font-mono text-sm">{revealKey.secret}</span>
                  <ButtonCopy value={revealKey.secret} className="bg-transparent" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Publishable key</Label>
                <div className="mt-1 flex items-center justify-between gap-2 rounded-md border bg-muted px-3 py-2">
                  <span className="truncate font-mono text-sm">{revealKey.publishableKey}</span>
                  <ButtonCopy value={revealKey.publishableKey} className="bg-transparent" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Store these values in a secure secret manager. If you lose them, you will need to rotate the key.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRevealKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
